import type { Framing } from '../components/StudioImage'
import { CONTENT_SECTIONS, UNPLACED_HEADING, UNPLACED_NOTE } from './contentManifest'

export type ContentMap = Record<
  string,
  {
    title: string | null
    body: string
    /**
     * How the photograph sits, when this block is one (D-199). Resolved
     * server-side: the placement override, then the photo's own, then centred.
     */
    framing?: Framing | null
  }
>

/** Where the draft projection lives. Under `/admin`, deliberately — see below. */
export const DRAFT_CONTENT_ROUTE = '/api/admin/content/draft'

/**
 * Whether this document is the editor's preview frame rather than the site.
 *
 * Read from the URL rather than passed as a prop, because the frame loads the
 * **real** public routes — `/site`, `/about`, `/classes` — and there is no React
 * boundary between the editor and what is inside the frame to pass anything
 * across (D-243). One render path is the whole guarantee that the preview is not
 * lying.
 */
export function isPreviewFrame(): boolean {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('preview') === '1'
}

/**
 * Where a public link should point when the shell is inside the preview frame.
 *
 * <b>Signed in, `/` redirects to the calendar.</b> The frame renders the real
 * public shell with its real links, so clicking the site's own *Home* inside the
 * preview replaced her page with the studio's internal calendar — under a
 * heading reading "Your page, with your changes". The same trap was already
 * known and fixed for the *View the site* button; this is the other door into
 * it (D-243).
 *
 * Carrying `?preview=1` across keeps her in drafts as she moves between pages,
 * which also makes the preview navigable rather than a single fixed shot.
 */
export function publicHref(path: string): string {
  if (!isPreviewFrame()) return path
  return `${path === '/' ? '/site' : path}?preview=1`
}

/**
 * The site's words: published for a visitor, drafted inside the preview frame.
 *
 * <b>The draft route is a separate endpoint, and that is the trap worth knowing
 * about.</b> `/api/content` is `AllowAnonymous` *and* `AllowKiosk` — the second
 * for the room screen's logo (D-232) — so a `?draft=1` parameter on it would
 * have put half-written sentences on a wall tablet and on the open internet. The
 * draft route requires `EditSiteContent` and is marked neither (D-233).
 */
export async function fetchContent(): Promise<ContentMap> {
  const preview = isPreviewFrame()
  const res = await fetch(preview ? DRAFT_CONTENT_ROUTE : '/api/content')

  if (!res.ok) {
    throw new Error(
      preview
        ? `Could not load your unpublished changes (${res.status})`
        : `Could not load the page (${res.status})`,
    )
  }

  return res.json()
}

/**
 * The same map the preview frame will render, fetched by the editor itself.
 *
 * The editor asks so that it can tell whether the frame is going to work before
 * it renders one. A frame that fails to load its content shows the visitor's
 * page under a heading claiming to show her changes, which is the confident
 * statement about absent data D-209 forbids — and after a deploy that is the
 * normal case, not the rare one (D-33).
 */
export async function fetchDraftContent(): Promise<ContentMap> {
  const res = await fetch(DRAFT_CONTENT_ROUTE)
  if (!res.ok) throw new Error(`Could not load your unpublished changes (${res.status})`)
  return res.json()
}

/** The framing for an image block, or null when it is not an image. */
export function framing(content: ContentMap | undefined, key: string): Framing | null {
  return content?.[key]?.framing ?? null
}

/**
 * Reads a content block, falling back to nothing rather than to a key.
 *
 * A missing block renders as absent, never as `home.hero.headline` on a live
 * homepage — the page has to survive a block Tonya deleted or a release that
 * added one before the seeder ran (A-16).
 */
export function text(content: ContentMap | undefined, key: string, fallback = ''): string {
  return content?.[key]?.body?.trim() || fallback
}

/** Whether a block has anything worth rendering a section for. */
export function has(content: ContentMap | undefined, key: string): boolean {
  return Boolean(content?.[key]?.body?.trim())
}

/**
 * A content block with its numbers filled in — "3 missed · 1 booked".
 *
 * <b>The sentence is Tonya's; the numbers are the server's</b> (A-16). Writing
 * "{missed} missed" in the component would make the wording a developer's to
 * change, which is the support call NFR-14 exists to prevent — but the counts
 * cannot live in a content block, so they arrive as merge values instead.
 *
 * <b>An unfilled field is left visible as `{{field}}`</b>, matching what the
 * notification dispatcher already does. Obviously broken beats a sentence with
 * a hole in it that reads almost right and ships unnoticed.
 */
export function fill(
  content: ContentMap | undefined,
  key: string,
  values: Record<string, string | number>,
  fallback = '',
): string {
  const body = text(content, key, fallback)
  if (!body) return body

  return body.replace(/\{\{(\w+)\}\}/g, (whole, field: string) =>
    Object.hasOwn(values, field) ? String(values[field]) : whole,
  )
}

/**
 * A content block as a list of points (D-202).
 *
 * <b>The studio's own information is mostly lists.</b> Their About page is four
 * bulleted sections — eight separate attire rules, one per kind of class — and
 * carrying them across as prose paragraphs turned "what shoes does my
 * five-year-old need?" into something you have to read a paragraph to answer.
 *
 * One point per line, so Tonya writes it in the editor exactly as it reads on
 * the page, with no markup to learn. A leading bullet or dash is tolerated
 * because she will paste from the old site and it is there.
 */
export function lines(content: ContentMap | undefined, key: string): string[] {
  return (content?.[key]?.body ?? '')
    .split('\n')
    .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
    .filter(Boolean)
}

/**
 * A content block as prose paragraphs (D-240).
 *
 * <b>A biography is not one sentence.</b> Every long block on the public site
 * rendered inside a single `<p>`, so anything with a blank line in it arrived
 * as one run-on wall — invisible for as long as it went untested, because
 * `about.owner1.bio` is seeded empty and nobody had written a real one.
 *
 * Blank-line separated, which is how a person types prose and how it pastes out
 * of the old site. Distinct from `lines()`: that one is for the studio's
 * bulleted lists, where every line is its own point (D-202). Prose that happens
 * to wrap is not a list, and rendering it as one is why this needs two
 * functions rather than a flag.
 */
export function paragraphs(content: ContentMap | undefined, key: string): string[] {
  return (content?.[key]?.body ?? '')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export type ContentBlockView = {
  key: string
  label: string | null
  title: string | null
  body: string
  publishedBody: string | null
  hasUnpublishedChanges: boolean
  publishedAt: string | null
  /**
   * The photograph this block points at, for the editor to frame.
   *
   * The *draft* id, which is what the editor is showing — `/api/content` sends
   * the published one instead, and conflating them is what put an unpublished
   * photograph's crop on the live site (D-253).
   */
  mediaAssetId: string | null
}

export async function fetchContentBlocks(): Promise<ContentBlockView[]> {
  const res = await fetch('/api/admin/content')
  if (!res.ok) throw new Error(`Could not load the content (${res.status})`)
  return res.json()
}

export async function saveContent(key: string, body: string): Promise<void> {
  const res = await fetch(`/api/admin/content/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body, title: null }),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
}

export async function publishContent(keys: string[]): Promise<void> {
  const res = await fetch('/api/admin/content/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(keys),
  })

  if (!res.ok) throw new Error(`Could not publish (${res.status})`)
}

export async function discardDraft(key: string): Promise<void> {
  const res = await fetch(`/api/admin/content/${encodeURIComponent(key)}/discard`, {
    method: 'POST',
  })

  if (!res.ok) throw new Error(`Could not discard that (${res.status})`)
}

/**
 * Groups blocks into the sections of the public site, in page order (D-241).
 *
 * <b>Replaces `byArea`, which grouped by the first segment of the key.</b> That
 * was a deliberate choice to avoid keeping a second copy of the grouping — and
 * it was wrong twice over. The key encodes the grouping but **not the order**,
 * so the list arrived alphabetically: `home.point1.body` above
 * `home.point1.title`, the About page very nearly reversed. And the prefix map
 * it needed anyway had gone stale, rendering `announcement`, `register` and
 * `import` as bare lowercase fragments with nothing catching it.
 *
 * The order now comes from `contentManifest.ts`, which is pinned against the
 * real block list by a test. Anything the manifest does not place is still
 * listed, last, under a heading that says so.
 *
 * <b>`page` narrows it to one page's words (D-260).</b> Sixty-seven fields in
 * one column meant scrolling past two pages' worth to reach the third, while a
 * preview of the page in question sat at the top of the screen. Passing the
 * path the preview is showing keeps the two halves of this screen talking about
 * the same page.
 *
 * <b>Filtering never hides a field behind a page it is not on.</b> A section
 * with no `pages` — the parent portal's link, the import notes, and anything
 * the manifest has never heard of — is not part of the website and always
 * shows. Neither does an unrecognised path: links inside the preview frame
 * navigate, so it can be somewhere that is not one of the three, and the honest
 * answer there is everything rather than nothing.
 */
export function bySection(
  blocks: ContentBlockView[],
  page?: string,
): { area: string; note?: string; blocks: ContentBlockView[] }[] {
  const remaining = new Map(blocks.map((block) => [block.key, block]))
  const sections: { area: string; note?: string; blocks: ContentBlockView[] }[] = []

  const known = CONTENT_SECTIONS.some((s) => s.pages?.includes(page ?? ''))

  for (const section of CONTENT_SECTIONS) {
    /*
     * Skipped, but its keys are still claimed — otherwise every block on the
     * other two pages would fall through to "Not placed on a page yet", which
     * says nobody recorded where they appear. That is a lie about sixty
     * fields whose home this file knows perfectly well.
     */
    const hidden = known && section.pages !== undefined && !section.pages.includes(page!)

    const found: ContentBlockView[] = []

    for (const key of section.keys) {
      const block = remaining.get(key)
      if (!block) continue

      found.push(block)
      remaining.delete(key)
    }

    // A section whose blocks have all been retired renders as nothing, rather
    // than as a heading with a void under it.
    if (found.length > 0 && !hidden) {
      sections.push({ area: section.heading, note: section.note, blocks: found })
    }
  }

  if (remaining.size > 0) {
    sections.push({
      area: UNPLACED_HEADING,
      note: UNPLACED_NOTE,
      blocks: [...remaining.values()],
    })
  }

  return sections
}

export function unpublishedCount(blocks: ContentBlockView[]): number {
  return blocks.filter((b) => b.hasUnpublishedChanges).length
}
